import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Download, Calendar, CheckCircle, Clock, FileText } from 'lucide-react';
import { toast } from 'sonner';

const BillingDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      // Load current user/tenant info
      const userRes = await axios.get('/auth/me');
      const user = userRes.data;
      
      // Load subscription plans
      const plansRes = await axios.get('/subscription-plans');
      setPlans(plansRes.data);
      
      // Mock current plan - in production would load from tenant
      setCurrentPlan({
        name: 'BUSINESS',
        status: 'active',
        next_billing_date: '2026-01-09',
        amount: 79.60,
        vehicles: 4
      });
      
      // Load invoices
      // TODO: Create endpoint to get tenant invoices
      setInvoices([]);
      
    } catch (error) {
      console.error('Error loading billing data:', error);
      toast.error('Fehler beim Laden der Abrechnungsdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (planId) => {
    try {
      setLoading(true);
      
      const origin = window.location.origin;
      const response = await axios.post('/billing/create-checkout', {
        plan_id: planId,
        vehicle_count: 4, // Current vehicle count
        branch_count: 1,  // Current branch count
        origin_url: origin
      });
      
      if (response.data.success) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Fehler beim Erstellen der Zahlung');
      setLoading(false);
    }
  };

  const getPlanBadge = (plan) => {
    const colors = {
      starter: 'bg-green-900/30 text-green-400 border-green-700',
      business: 'bg-blue-900/30 text-blue-400 border-blue-700',
      enterprise: 'bg-purple-900/30 text-purple-400 border-purple-700'
    };
    return colors[plan.plan_id?.replace('plan_', '')] || 'bg-zinc-800 text-zinc-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-4xl font-bold text-white">Abrechnung</h1>
        <p className="text-zinc-400 mt-2">Verwalten Sie Ihr Abonnement und Rechnungen</p>
      </div>

      {/* Current Plan */}
      {currentPlan && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Aktueller Plan</CardTitle>
                <CardDescription>Ihr Abonnement-Status</CardDescription>
              </div>
              <Badge className={`${currentPlan.status === 'active' ? 'bg-green-900/30 text-green-400 border-green-700' : 'bg-zinc-800'} border`}>
                {currentPlan.status === 'active' ? (
                  <>
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Aktiv
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3 mr-1" />
                    Trial
                  </>
                )}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-zinc-400 mb-1">Plan</p>
                <p className="text-2xl font-bold text-white">{currentPlan.name}</p>
              </div>
              <div>
                <p className="text-sm text-zinc-400 mb-1">Monatliche Kosten</p>
                <p className="text-2xl font-bold text-white">€{currentPlan.amount.toFixed(2)}</p>
                <p className="text-xs text-zinc-500 mt-1">{currentPlan.vehicles} Fahrzeuge × €19,90</p>
              </div>
              <div>
                <p className="text-sm text-zinc-400 mb-1">Nächste Abrechnung</p>
                <p className="text-lg font-semibold text-white">
                  {new Date(currentPlan.next_billing_date).toLocaleDateString('de-DE')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-4">Verfügbare Pläne</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.plan_id} className={`bg-zinc-900/50 border-2 ${getPlanBadge(plan)}`}>
              <CardHeader>
                <CardTitle className="text-xl text-white">{plan.name}</CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-black text-white">€{plan.price_per_vehicle}</span>
                  <span className="text-zinc-400 ml-2">/Fahrzeug/Monat</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-zinc-300">{feature}</p>
                    </div>
                  ))}
                </div>
                
                {/* Pricing Details */}
                <div className="pt-4 border-t border-zinc-700">
                  <p className="text-xs text-zinc-400 mb-2">Zusätzliche Kosten:</p>
                  <p className="text-sm text-zinc-300">+ €{plan.price_per_branch} pro zusätzlicher Filiale</p>
                  <p className="text-xs text-zinc-500 mt-1">Erste Filiale inklusive</p>
                </div>
                
                {currentPlan?.name !== plan.name && (
                  <Button 
                    onClick={() => handleUpgrade(plan.plan_id)} 
                    className="w-full"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Upgrade auf {plan.name}
                      </>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Invoices */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white">Rechnungen</CardTitle>
              <CardDescription>Ihre Abrechnungshistorie</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">Noch keine Rechnungen vorhanden</p>
              <p className="text-sm text-zinc-500 mt-2">
                Ihre Rechnungen erscheinen hier nach der ersten Zahlung
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <div
                  key={invoice.invoice_id}
                  className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <FileText className="h-8 w-8 text-zinc-500" />
                    <div>
                      <p className="font-semibold text-white">{invoice.invoice_number}</p>
                      <p className="text-sm text-zinc-400">
                        {new Date(invoice.created_at).toLocaleDateString('de-DE')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-lg font-bold text-white">€{invoice.amount.toFixed(2)}</p>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingDashboard;
